import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkle, Lightning, Target } from "@phosphor-icons/react";
import { TOOLS } from "../lib/tools";
import AuditHero from "../components/AuditHero";

const ANALYTICS_IMG =
  "https://images.unsplash.com/photo-1686061593213-98dad7c599b9?crop=entropy&cs=srgb&fm=jpg&q=85";
const OFFICE_IMG =
  "https://images.unsplash.com/photo-1559136656-3db4bf6c35f8?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Landing() {
  return (
    <div>
      {/* HERO with AUDIT FORM */}
      <section className="relative border-b border-[#E5E7EB]">
        <div className="container-wide pt-12 md:pt-20 pb-12 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
            <div className="lg:col-span-7 reveal">
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-5">
                <span className="inline-block h-1.5 w-1.5 bg-[#002FA7] mr-2 align-middle" />
                Marketing AI
              </p>
              <h1 className="font-display text-4xl sm:text-5xl md:text-7xl font-black leading-[0.95] tracking-tighter">
                Ten marketing tools.<br />
                <span className="text-[#002FA7]">One</span> intelligent studio.
              </h1>
              <p className="mt-5 sm:mt-6 max-w-xl text-base sm:text-lg text-[#525252] leading-relaxed">
                From SEO audits to ad copy, review responses to brand monitoring —
                run every marketing task with a single, intelligent workspace.
              </p>
              <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
                <Link to="/login" className="btn-primary" data-testid="hero-cta-primary">
                  Launch the Studio <ArrowRight size={18} weight="bold" />
                </Link>
                <Link to="/pricing" className="btn-secondary" data-testid="hero-cta-secondary">
                  See pricing
                </Link>
              </div>

              <div className="mt-10 sm:mt-14 grid grid-cols-3 gap-4 sm:gap-8 max-w-lg">
                <Stat label="Tools" value="10" />
                <Stat label="Free tools" value="2" />
                <Stat label="Avg. time saved" value="12h/wk" />
              </div>
            </div>

            <div className="lg:col-span-5">
              <AuditHero />
            </div>
          </div>
        </div>
      </section>

      {/* REAL PROJECT PROOF STRIP */}
      <section className="border-b border-[#E5E7EB] bg-[#F5F5F5]">
        <div className="container-wide py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
            Built from real product experience
          </p>
          <div className="flex gap-5 sm:gap-10 font-display text-base sm:text-xl font-extrabold tracking-tight text-[#0A0A0A] opacity-80 flex-wrap">
            <span>PCV Logbook</span>
            <span>CV Bright</span>
            <span>MeliBus</span>
          </div>
        </div>
      </section>

      {/* TOOLS GRID — Tetris */}
      <section className="container-wide py-14 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8 md:mb-12">
          <div className="max-w-xl">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
              The Toolkit
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
              Built for every stage of growth.
            </h2>
          </div>
          <p className="text-[#525252] max-w-sm text-sm md:text-base">
            From incorporation to brand monitoring — every tool ships with clean defaults
            and a prompt you can actually re-use.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 sm:gap-6">
          <FeatureCard tool={TOOLS.find((t) => t.key === "seo")} image={ANALYTICS_IMG} className="md:col-span-8 lg:col-span-8" />
          <FeatureCard tool={TOOLS.find((t) => t.key === "ads")} className="md:col-span-4 lg:col-span-4" tall />

          <ToolCard tool={TOOLS.find((t) => t.key === "social")} className="md:col-span-4 lg:col-span-4" />
          <ToolCard tool={TOOLS.find((t) => t.key === "reviews")} className="md:col-span-4 lg:col-span-4" />
          <ToolCard tool={TOOLS.find((t) => t.key === "local")} className="md:col-span-4 lg:col-span-4" />

          <ToolCard tool={TOOLS.find((t) => t.key === "brand")} className="md:col-span-4 lg:col-span-3" />
          <ToolCard tool={TOOLS.find((t) => t.key === "starter")} className="md:col-span-4 lg:col-span-3" />
          <ToolCard tool={TOOLS.find((t) => t.key === "formation")} className="md:col-span-4 lg:col-span-3" />
          <ToolCard tool={TOOLS.find((t) => t.key === "growth")} className="md:col-span-4 lg:col-span-3" />

          <ToolCard tool={TOOLS.find((t) => t.key === "relate")} className="md:col-span-4 lg:col-span-6" />
          <ToolCard tool={TOOLS.find((t) => t.key === "source")} className="md:col-span-4 lg:col-span-6" />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-[#E5E7EB] bg-[#F5F5F5]">
        <div className="container-wide py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
              How it works
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
              Pick a tool. Fill three fields. Ship.
            </h2>
            <p className="text-[#525252] mt-5">
              Every tool is a precisely-tuned prompt wrapped in a clean interface. Results are
              streamable, copyable, and logged to your private history.
            </p>
            <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-[#002FA7] font-bold" data-testid="how-cta">
              Open the studio <ArrowUpRight weight="bold" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Step n="01" title="Choose a tool" icon={<Target size={20} weight="duotone" />} text="Ten specialists across foundation, growth and strategy." />
            <Step n="02" title="Give context" icon={<Sparkle size={20} weight="duotone" />} text="Brand, audience, offer — the tool asks only what it needs." />
            <Step n="03" title="Ship the output" icon={<Lightning size={20} weight="duotone" />} text="Copy, export to PDF, or iterate. History keeps every generation." />
          </div>
        </div>
      </section>

      {/* BIG CTA */}
      <section className="container-wide py-14 md:py-24">
        <div className="relative border border-[#0A0A0A] bg-[#0A0A0A] text-white p-8 sm:p-12 md:p-20 overflow-hidden">
          <div className="absolute inset-0 opacity-40 mix-blend-overlay">
            <img src={OFFICE_IMG} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-white/60 mb-4">
              Start for free
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
              Stop stitching tools. Start shipping campaigns.
            </h2>
            <p className="mt-5 text-white/75 max-w-xl">
              Ten AI-powered tools, one workspace, zero context-switching.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/login" className="btn-primary bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]" data-testid="bottom-cta-primary">
                Launch the studio <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/pricing" className="btn-secondary border-white text-white hover:bg-white hover:text-[#0A0A0A]" data-testid="bottom-cta-secondary">
                Compare plans
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-t border-[#0A0A0A] pt-3">
      <div className="font-display text-xl sm:text-3xl font-extrabold">{value}</div>
      <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.18em] text-[#737373] mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, icon, text }) {
  return (
    <div className="bg-white border border-[#E5E7EB] p-6 h-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-[#737373]">{n}</span>
        <span className="text-[#002FA7]">{icon}</span>
      </div>
      <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
      <p className="text-sm text-[#525252]">{text}</p>
    </div>
  );
}

function FeatureCard({ tool, image, tall, className }) {
  const Icon = tool.icon;
  return (
    <Link
      to={`/tools/${tool.key}`}
      className={`tool-card ${className || ""} ${tall ? "lg:row-span-1" : ""}`}
      data-testid={`tool-card-${tool.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="chip">{tool.category}</span>
          <ToolBadge free={tool.free} />
        </div>
        <ArrowUpRight size={18} weight="bold" className="text-[#0A0A0A] group-hover:text-[#002FA7]" />
      </div>
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 bg-[#002FA7] text-white flex items-center justify-center shrink-0">
          <Icon size={22} weight="duotone" />
        </div>
        <div>
          <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">
            {tool.name}
          </h3>
          <p className="text-[#525252] mt-2">{tool.tagline}</p>
        </div>
      </div>
      {image && (
        <div className="mt-4 border-t border-[#E5E7EB] -mx-7 -mb-7">
          <img src={image} alt="" className="w-full h-52 object-cover" />
        </div>
      )}
      {tall && !image && (
        <div className="mt-auto pt-6">
          <ul className="space-y-2 text-sm text-[#525252]">
            <li>— Google RSA headlines</li>
            <li>— Meta primary text variants</li>
            <li>— Targeting &amp; budget hints</li>
          </ul>
        </div>
      )}
    </Link>
  );
}

function ToolCard({ tool, className }) {
  const Icon = tool.icon;
  return (
    <Link
      to={`/tools/${tool.key}`}
      className={`tool-card group ${className || ""}`}
      data-testid={`tool-card-${tool.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="chip">{tool.category}</span>
          <ToolBadge free={tool.free} />
        </div>
        <ArrowUpRight size={16} weight="bold" className="text-[#737373] group-hover:text-[#002FA7]" />
      </div>
      <div className="h-9 w-9 bg-[#0A0A0A] text-white flex items-center justify-center">
        <Icon size={18} weight="duotone" />
      </div>
      <h3 className="font-display text-xl font-bold tracking-tight">{tool.name}</h3>
      <p className="text-sm text-[#525252]">{tool.tagline}</p>
    </Link>
  );
}

function ToolBadge({ free }) {
  return free ? (
    <span className="inline-flex items-center px-1.5 py-0.5 bg-[#002FA7] text-white font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
      Free
    </span>
  ) : (
    <span className="inline-flex items-center px-1.5 py-0.5 border border-[#0A0A0A] text-[#0A0A0A] font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
      Pro
    </span>
  );
}
